import type { WorkflowGroupOptions } from '../types';
import { WorkflowGroupManager } from '../WorkflowGroupManager';
import networkWorkflowOptions from './carrierSelection';
import addCarrierAccountWorkflows from './carrierAccounts';
import carrierSelectionOverrides from './carrierSelectionOverrides';
import scheduleWorkflowOptions from './schedules';
import addressWorkflowOptions from './addresses';
import contactWorkflowOptions from './contacts';
import carrierSettingsWorkflowOptions from './carrierSettings';
import contractWorkflowOptions from './contracts';
import fulfillmentCenterWorkflowOptions from './fulfillmentCenters';
import labelProperties from './labelProperties';
import labelAugmentations from './labelAugmentations';
import tagWorkflowOptions from './tags';
import packagingOptions from './packaging';
import packagingSetOptions from './packagingSets';
import carrierRateSets from './carrierRateSets';
import returnsSettings from './returnsSettings';
import augmentationSet from './augmentationSets';
import tenantOptions from './tenant';
import thirdPartyBillingAccountOptions from './thirdPartyBillingAccount';
import thirdPartyBillingSetOptions from './thirdPartyBillingAccountSet';
import returnToAddressOverridesOptions from './returnToAddressOverrides';
import carrierShipmentLaneConfigurationOptions from './carrierShipmentLaneConfigurations';
import { BaseFeatureToggles } from '../../navigation/BaseFeatureToggles';
export const workflowGroupsOptions: WorkflowGroupOptions[] = [
  {
    workflowGroupKey: 'addresses',
    workflowGroupName: 'Addresses',
    workflowGroupIcon: 'address',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription: 'Addresses are used to define origin and return nodes for your fulfillment network.',
    workflowGroupIndex: 1,
    required: true,
    workflowGroupWorkflowOptions: addressWorkflowOptions,
  },
  {
    workflowGroupKey: 'schedules',
    workflowGroupName: 'Schedules',
    workflowGroupIcon: 'schedule',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription: 'Defines the shipping days, processing times and holidays for your origins.',
    workflowGroupIndex: 2,
    required: true,
    workflowGroupWorkflowOptions: scheduleWorkflowOptions,
  },
  {
    workflowGroupKey: 'contacts',
    workflowGroupName: 'Contacts',
    workflowGroupIcon: 'contact',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription: 'Shared Email and Phone Numbers used for Carrier Integration.',
    workflowGroupIndex: 3,
    required: true,
    workflowGroupWorkflowOptions: contactWorkflowOptions,
  },
  {
    workflowGroupKey: 'fulfillmentCenters',
    workflowGroupName: 'Origins',
    workflowGroupIcon: 'fulfillmentCenter',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription: 'Defines the address and contact information for a fulfillment center in your network.',
    workflowGroupIndex: 0,
    required: true,
    workflowGroupWorkflowOptions: fulfillmentCenterWorkflowOptions,
  },
  {
    workflowGroupKey: 'carrierSettings',
    workflowGroupName: 'Carrier Settings',
    workflowGroupIcon: 'carrierSettings',
    workflowGroupCategory: 'carrier',
    workflowGroupDescription: 'Defines global carrier settings that are shared for all carrier service methods.',
    workflowGroupIndex: 4,
    required: true,
    workflowGroupWorkflowOptions: carrierSettingsWorkflowOptions,
  },
  {
    workflowGroupKey: 'carrierAccounts',
    workflowGroupName: 'Carrier Accounts',
    workflowGroupIcon: 'carrierAccount',
    workflowGroupCategory: 'carrier',
    workflowGroupDescription: 'Defines the credentials to be used when Shipium makes an API call to a carrier.',
    workflowGroupIndex: 5,
    required: true,
    workflowGroupWorkflowOptions: addCarrierAccountWorkflows,
  },
  {
    workflowGroupKey: 'carrierRateSets',
    workflowGroupName: 'Carrier Rate Sets',
    workflowGroupIcon: 'carrierRateSet',
    workflowGroupCategory: 'carrier',
    workflowGroupDescription: '',
    workflowGroupIndex: 7,
    shipiumOnlyFeatureToggleRestricted: true,
    required: true,
    workflowGroupWorkflowOptions: carrierRateSets,
  },
  {
    workflowGroupKey: 'returnsSettings',
    workflowGroupName: 'Returns',
    workflowGroupIcon: 'returns',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription:
      'Defines the address and contact information for a Return node in your fulfillment network.',
    workflowGroupIndex: 9,
    required: false,
    workflowGroupWorkflowOptions: returnsSettings,
  },
  {
    workflowGroupKey: 'carrierShipmentLaneConfigurations',
    workflowGroupName: 'Carrier Shipment Lane Configurations',
    workflowGroupIcon: 'carrierShipmentLaneConfiguration',
    workflowGroupCategory: 'fulfillmentCenter',
    workflowGroupDescription: 'Defines the configuration for a carrier shipment lane on your origin.',
    workflowGroupIndex: 10,
    required: false,
    workflowGroupWorkflowOptions: carrierShipmentLaneConfigurationOptions,
    requiredFeatures: { [BaseFeatureToggles.SHIPMENT_LANES]: 'true' },
  },
  {
    workflowGroupKey: 'contracts',
    workflowGroupName: 'Contracts',
    workflowGroupIcon: 'contract',
    workflowGroupCategory: 'carrier',
    workflowGroupDescription:
      'Defines the carrier account, rates and carrier settings that will be used to cost shipments for a specific location/context.',
    workflowGroupIndex: 10,
    required: true,
    workflowGroupWorkflowOptions: contractWorkflowOptions,
  },
  {
    workflowGroupKey: 'labelProperties',
    workflowGroupName: 'Label Properties',
    workflowGroupIcon: 'labelProperties',
    workflowGroupCategory: 'label',
    workflowGroupDescription: 'Defines the dimensions, orientation and DPI to be used when generating labels.',
    workflowGroupIndex: 11,
    required: true,
    workflowGroupWorkflowOptions: labelProperties,
  },
  {
    workflowGroupKey: 'labelAugmentations',
    workflowGroupName: 'Label Augmentations',
    workflowGroupIcon: 'labelAugmentation',
    workflowGroupCategory: 'label',
    workflowGroupDescription: 'Optional Zebra printer instructions to be applied when generating labels.',
    workflowGroupIndex: 12,
    required: false,
    workflowGroupWorkflowOptions: labelAugmentations,
  },
  {
    workflowGroupKey: 'augmentationSet',
    workflowGroupName: 'Label Augmentation Rules',
    workflowGroupIcon: 'augmentationSet',
    workflowGroupCategory: 'label',
    workflowGroupDescription: 'Defines a set of rules for applying label augmentations to labels.',
    workflowGroupIndex: 13,
    required: false,
    workflowGroupWorkflowOptions: augmentationSet,
  },
  {
    workflowGroupKey: 'carrierSelection',
    workflowGroupName: 'Fulfillment Contexts',
    workflowGroupIcon: 'carrierSelection',
    workflowGroupCategory: 'shiptime',
    workflowGroupDescription: `Defines configuration sets to be used by the Shiptime API. At least one fulfillment context is required for carrier selection and label generation calls.`,
    workflowGroupIndex: 14,
    required: true,
    workflowGroupWorkflowOptions: networkWorkflowOptions,
  },
  {
    workflowGroupKey: 'carrierSelectionOverrides',
    workflowGroupName: 'Carrier Selection Overrides',
    workflowGroupIcon: 'carrierSelectionOverrides',
    workflowGroupCategory: 'shiptime',
    workflowGroupDescription:
      'Optional overides that modify the behavior of carrier selection when making calls to the Shiptime API.',
    workflowGroupIndex: 15,
    required: false,
    workflowGroupWorkflowOptions: carrierSelectionOverrides,
  },
  {
    workflowGroupKey: 'tenant',
    workflowGroupName: 'Tenants',
    workflowGroupIcon: '',
    workflowGroupDescription: 'Configure Tenants',
    workflowGroupCategory: 'shiptime',
    workflowGroupIndex: 20,
    required: false,
    workflowGroupWorkflowOptions: tenantOptions,
    requiredFeatures: { [BaseFeatureToggles.TENANT]: 'true' },
  },
  //holding off until we are ready for third party billing release
  {
    workflowGroupKey: 'thirdPartyBillingAccountSets',
    workflowGroupName: 'Third Party Billing Groups',
    workflowGroupIcon: 'carrierSelection',
    workflowGroupCategory: 'shiptime',
    // TODO work on copy
    workflowGroupDescription: `Configure details required to bill labels to a third party`,
    workflowGroupIndex: 21,
    required: false,
    workflowGroupWorkflowOptions: thirdPartyBillingSetOptions,
  },
  {
    workflowGroupKey: 'thirdPartyBillingAccounts',
    workflowGroupName: 'Third Party Billing Accounts',
    workflowGroupIcon: 'carrierSelection',
    workflowGroupCategory: 'shiptime',
    // TODO work on copy
    workflowGroupDescription: `Configure billing accounts for third parties`,
    workflowGroupIndex: 22,
    required: false,
    workflowGroupWorkflowOptions: thirdPartyBillingAccountOptions,
  },
  {
    workflowGroupKey: 'packaging',
    workflowGroupName: 'Packaging',
    workflowGroupIcon: 'packaging',
    workflowGroupCategory: 'packaging',
    // TODO work on copy
    workflowGroupDescription: `Configure individual packages for use`,
    workflowGroupIndex: 23,
    required: false,
    workflowGroupWorkflowOptions: packagingOptions,
    requiredFeatures: { [BaseFeatureToggles.PACKAGING_WORKFLOWS]: 'true' },
  },
  {
    workflowGroupKey: 'packagingSet',
    workflowGroupName: 'Packaging Set',
    workflowGroupIcon: 'packagingSet',
    workflowGroupCategory: 'packaging',
    // TODO work on copy
    workflowGroupDescription: `Configure groups of packages to be used together`,
    workflowGroupIndex: 24,
    required: false,
    workflowGroupWorkflowOptions: packagingSetOptions,
    requiredFeatures: { [BaseFeatureToggles.PACKAGING_WORKFLOWS]: 'true' },
  },
  {
    workflowGroupKey: 'tags',
    workflowGroupName: 'Tags',
    workflowGroupIcon: 'tag',
    workflowGroupCategory: 'shiptime',
    workflowGroupDescription: 'Configure Tags',
    workflowGroupIndex: 100,
    required: false,
    workflowGroupWorkflowOptions: tagWorkflowOptions,
  },
  {
    workflowGroupKey: 'returnToAddressOverrides',
    workflowGroupName: 'Return to Address Overrides',
    workflowGroupIcon: 'returnToAddressOverrides',
    workflowGroupCategory: 'shiptime',
    workflowGroupDescription: 'Configure return to address overrides',
    workflowGroupIndex: 101,
    required: false,
    workflowGroupWorkflowOptions: returnToAddressOverridesOptions,
  },
];

export const workflowGroupManager = new WorkflowGroupManager(workflowGroupsOptions);

export default workflowGroupManager;
